import { useState, useEffect, useContext, createContext, useCallback, useRef } from 'react';
import type { ExportStyle, ExportStyleRow } from '@dashai/types/export-style';
import { rowToExportStyle } from '@dashai/types/export-style';
import { STORAGE_BUCKET } from '@dashai/lib/firebase';

// `rowToExportStyle` builds public asset URLs from the Firebase Storage bucket.
// The stored `logo_main_path` / `cover_image_path` strings (e.g.
// `export-assets/red_bull/logo-main.png`) are now objects in Firebase Storage,
// made public.

// ── Retry configuration ──────────────────────────────────────────────────
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // exponential backoff
const FETCH_TIMEOUT = 8000; // 8s total timeout per attempt

// ── Module-level cache ──────────────────────────────────────────────────
// Shared across all hook instances — survives component remounts, tab switches
const styleCache = new Map<string, ExportStyle>();
// Track in-flight fetches to avoid duplicate requests for the same clientId
const inflightFetches = new Map<string, Promise<ExportStyle | null>>();

/**
 * Context for providing a pre-resolved ExportStyle (e.g., in shared canvas views
 * where the viewer may not be authenticated). When set, useExportStyle skips the
 * fetch and returns this style directly.
 */
export const ExportStyleOverrideContext = createContext<ExportStyle | null>(null);

/**
 * Fetch an export style from the Firebase `dashaiExportStyleResolve`
 * endpoint with timeout. Returns the resolved style or `null` on no
 * match. **Public endpoint, no auth required** — the hook is consumed
 * from share-link view pages that may be unauthenticated (PublicCanvasView
 * etc.). Throws when `VITE_FIREBASE_FUNCTIONS_URL` is unset — there is
 * no fallback. Backed by Firestore `export_styles/{styleId}`.
 */
async function fetchStyleFromServer(clientId: string): Promise<ExportStyle | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const fbBase = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
    if (!fbBase) throw new Error('VITE_FIREBASE_FUNCTIONS_URL is not set');
    // No auth — public endpoint.
    const response = await fetch(`${fbBase.replace(/\/+$/, '')}/dashaiExportStyleResolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId }),
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    if (data?.style) {
      return rowToExportStyle(data.style as ExportStyleRow, STORAGE_BUCKET);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch with retry and exponential backoff.
 * Deduplicates concurrent requests for the same clientId. Every caller shares
 * the request, so it takes no caller's abort signal.
 */
async function fetchStyleWithRetry(clientId: string): Promise<ExportStyle | null> {
  // Return cached result if available
  if (styleCache.has(clientId)) {
    return styleCache.get(clientId)!;
  }

  // Deduplicate: if there's already an in-flight fetch for this clientId, join it
  const inflight = inflightFetches.get(clientId);
  if (inflight) return inflight;

  const fetchPromise = (async () => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const style = await fetchStyleFromServer(clientId);
        if (style) {
          styleCache.set(clientId, style);
          return style;
        }
        return null; // Server returned no style — not an error, just no style configured
      } catch (e) {
        lastError = e as Error;
        // Don't retry after the per-attempt timeout aborts
        if ((e as Error).name === 'AbortError') return null;
        // Wait before retrying (skip delay on last attempt)
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
        }
      }
    }

    console.warn(`[useExportStyle] All ${MAX_RETRIES} attempts failed for "${clientId}":`, lastError?.message);
    throw lastError || new Error('Failed to fetch export style');
  })();

  inflightFetches.set(clientId, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    inflightFetches.delete(clientId);
  }
}

/**
 * Pre-fetch and cache an export style for a given clientId.
 * Call this proactively (e.g., on workspace change) to warm the cache
 * so that useExportStyle resolves synchronously when a deck mounts.
 */
export async function prefetchExportStyle(clientId: string): Promise<ExportStyle | null> {
  if (!clientId) return null;
  if (styleCache.has(clientId)) return styleCache.get(clientId)!;

  try {
    return await fetchStyleWithRetry(clientId);
  } catch {
    // Pre-fetch failure is non-critical — the hook will retry when the deck mounts
    return null;
  }
}

export function useExportStyle(clientId?: string) {
  const override = useContext(ExportStyleOverrideContext);

  // Initialize synchronously from cache or override — avoids flicker on cache hit
  const [style, setStyle] = useState<ExportStyle | null>(() => {
    if (override) return override;
    if (clientId && styleCache.has(clientId)) return styleCache.get(clientId)!;
    return null;
  });
  const [loading, setLoading] = useState(() => {
    if (override) return false;
    if (clientId && styleCache.has(clientId)) return false;
    return !!clientId; // loading = true if we have a clientId but no cached style
  });
  const [error, setError] = useState<string | null>(null);

  // Track the current clientId to detect stale responses
  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;

  // Shared abort controller — covers both initial fetch and manual retries
  const abortRef = useRef<AbortController | null>(null);

  const doFetch = useCallback(async (id: string, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    // The fetch is shared by every caller for this clientId, so this caller's
    // signal only makes it ignore the result. Cancelling the shared request
    // would hand an aborted fetch to the next caller that joins it.
    try {
      const resolved = await fetchStyleWithRetry(id);
      // Guard against stale responses (clientId changed during fetch)
      if (signal?.aborted || clientIdRef.current !== id) return;
      setStyle(resolved);
    } catch (e) {
      if (signal?.aborted || clientIdRef.current !== id) return;
      setError((e as Error).message || 'Failed to load client branding');
    } finally {
      if (!signal?.aborted && clientIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // If an override is provided via context, use it directly
    if (override) {
      setStyle(override);
      setLoading(false);
      setError(null);
      return;
    }

    if (!clientId) {
      setStyle(null);
      setLoading(false);
      setError(null);
      return;
    }

    // If cached, resolve synchronously
    if (styleCache.has(clientId)) {
      setStyle(styleCache.get(clientId)!);
      setLoading(false);
      setError(null);
      return;
    }

    // Fetch with retry. Drop the previous workspace's style first, so a fetch
    // that fails never leaves another workspace's branding on screen.
    setStyle(null);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    doFetch(clientId, abortRef.current.signal);

    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [clientId, override, doFetch]);

  const retry = useCallback(() => {
    if (!clientId) return;
    // Clear cache entry to force a fresh fetch
    styleCache.delete(clientId);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    doFetch(clientId, abortRef.current.signal);
  }, [clientId, doFetch]);

  return { style, loading, error, retry };
}
